import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Briefcase, CornerDownLeft, Search, TriangleAlert } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
    deviceTitle,
    deviceTypeChip,
    locationOnFile,
    matchDevices,
    sizeBadge,
    type EditorDevice,
} from "@/pages/commercial/estimate-lines/model";

/** What a pick means: a device, the "Whole job" group, or no device. */
export type DeviceMenuPick =
    | { kind: "device"; device: EditorDevice; work: "repair" | "replace" }
    | { kind: "whole" }
    | { kind: "none" };

type Row = { kind: "device"; device: EditorDevice } | { kind: "whole" } | { kind: "none" };

/**
 * The device menu (Faster Estimates, approved mockup): type to search, ↑ ↓ to
 * move, ← → for Repair / Replace, ↵ to pick, Esc to close. Each device shows
 * its size badge, its name, and type + serial chips — every device once (not a
 * Repair row and a Replace row each), with the serial up front.
 *
 *   mode "repair"  "Add repair for device ▾" — every row carries the Repair /
 *                  Replace switch.
 *   mode "tag"     "Tag to device ▾" / the line editor's device box — offers
 *                  "Whole job" and "No device" after the devices.
 */
export function DeviceMenu({
    devices,
    mode,
    onPick,
    trigger,
    open,
    onOpenChange,
    title,
    side = "bottom",
    align = "start",
    allowWholeJob = mode === "tag",
    allowNone = mode === "tag",
}: {
    devices: readonly EditorDevice[];
    mode: "repair" | "tag";
    onPick: (pick: DeviceMenuPick) => void;
    trigger: ReactNode;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    title?: string;
    side?: "top" | "bottom";
    align?: "start" | "end" | "center";
    allowWholeJob?: boolean;
    allowNone?: boolean;
}) {
    const [openInner, setOpenInner] = useState(false);
    const isOpen = open ?? openInner;
    const setOpen = (v: boolean) => {
        onOpenChange?.(v);
        if (open === undefined) setOpenInner(v);
    };
    return (
        <Popover open={isOpen} onOpenChange={setOpen}>
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
            <PopoverContent
                side={side}
                align={align}
                className="w-[380px] max-w-[calc(100vw-24px)] overflow-hidden p-0"
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                {isOpen && (
                    <DeviceMenuBody
                        devices={devices}
                        mode={mode}
                        title={title}
                        allowWholeJob={allowWholeJob}
                        allowNone={allowNone}
                        onPick={(p) => {
                            setOpen(false);
                            onPick(p);
                        }}
                        onClose={() => setOpen(false)}
                    />
                )}
            </PopoverContent>
        </Popover>
    );
}

export function DeviceMenuBody({
    devices,
    mode,
    title,
    allowWholeJob,
    allowNone,
    onPick,
    onClose,
}: {
    devices: readonly EditorDevice[];
    mode: "repair" | "tag";
    title?: string;
    allowWholeJob: boolean;
    allowNone: boolean;
    onPick: (pick: DeviceMenuPick) => void;
    onClose: () => void;
}) {
    const [q, setQ] = useState("");
    const [active, setActive] = useState(0);
    const [work, setWork] = useState<"repair" | "replace">("repair");
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const t = setTimeout(() => inputRef.current?.focus(), 0);
        return () => clearTimeout(t);
    }, []);

    const rows: Row[] = useMemo(() => {
        const hits: Row[] = matchDevices(devices, q).map((device) => ({ kind: "device", device }));
        const words = q.trim().toLowerCase();
        if (allowWholeJob && (!words || "whole job trip charge".includes(words))) hits.push({ kind: "whole" });
        if (allowNone && (!words || "no device none".includes(words))) hits.push({ kind: "none" });
        return hits;
    }, [devices, q, allowWholeJob, allowNone]);
    const at = Math.min(active, Math.max(0, rows.length - 1));

    useEffect(() => {
        listRef.current?.querySelector<HTMLElement>(`[data-row="${at}"]`)?.scrollIntoView?.({ block: "nearest" });
    }, [at]);

    const pick = (row: Row | undefined, w: "repair" | "replace" = work) => {
        if (!row) return;
        if (row.kind === "device") onPick({ kind: "device", device: row.device, work: w });
        else onPick({ kind: row.kind });
    };

    return (
        <div role="dialog" aria-label={title ?? (mode === "repair" ? "Add repair for device" : "Tag to device")}>
            <div className="flex items-center gap-2 border-b px-3 py-2.5 text-muted-foreground">
                <Search className="h-4 w-4 shrink-0" />
                <input
                    ref={inputRef}
                    type="text"
                    value={q}
                    aria-label="Find device"
                    placeholder="Size, type or serial"
                    autoComplete="off"
                    className="min-w-0 flex-1 bg-transparent text-sm font-medium text-foreground outline-none placeholder:text-muted-foreground/70"
                    onChange={(e) => {
                        setQ(e.target.value);
                        setActive(0);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setActive(Math.min(rows.length - 1, at + 1));
                        } else if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setActive(Math.max(0, at - 1));
                        } else if (mode === "repair" && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
                            e.preventDefault();
                            setWork(e.key === "ArrowLeft" ? "repair" : "replace");
                        } else if (e.key === "Enter") {
                            e.preventDefault();
                            pick(rows[at]);
                        } else if (e.key === "Escape") {
                            e.preventDefault();
                            onClose();
                        }
                    }}
                />
                <Kbd>esc</Kbd>
            </div>
            <div ref={listRef} role="listbox" className="max-h-[300px] overflow-auto p-1.5">
                {rows.length === 0 ? (
                    <div className="px-4 py-5 text-center text-sm text-muted-foreground">
                        No device matches &ldquo;{q}&rdquo;
                    </div>
                ) : (
                    <>
                        <div className="px-2 pb-1 pt-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground/80">
                            {mode === "repair" ? "Devices at this site" : "Tag to"}
                        </div>
                        {rows.map((row, i) => {
                            const on = i === at;
                            return (
                                <div
                                    key={row.kind === "device" ? row.device.id : row.kind}
                                    data-row={i}
                                    role="option"
                                    aria-selected={on}
                                    className={cn(
                                        "flex cursor-pointer items-center gap-2.5 rounded-lg p-2",
                                        on && "bg-primary/10",
                                    )}
                                    onMouseMove={() => i !== at && setActive(i)}
                                    onClick={() => pick(row)}
                                >
                                    {row.kind === "device" ? (
                                        <DeviceRow device={row.device} />
                                    ) : row.kind === "whole" ? (
                                        <>
                                            <SizeBadge>
                                                <Briefcase className="h-3.5 w-3.5" />
                                            </SizeBadge>
                                            <span className="flex min-w-0 flex-col leading-tight">
                                                <b className="text-[13px] font-bold">Whole job</b>
                                                <small className="text-[11px] text-muted-foreground">
                                                    Trip charge and other visit costs
                                                </small>
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <SizeBadge tone="warn">
                                                <TriangleAlert className="h-3.5 w-3.5" />
                                            </SizeBadge>
                                            <b className="text-[13px] font-bold">No device</b>
                                        </>
                                    )}
                                    {mode === "repair" && row.kind === "device" ? (
                                        <span
                                            className={cn(
                                                "ml-auto inline-flex shrink-0 rounded-lg bg-muted p-0.5",
                                                !on && "opacity-60",
                                            )}
                                        >
                                            {(["repair", "replace"] as const).map((w) => (
                                                <button
                                                    key={w}
                                                    type="button"
                                                    tabIndex={-1}
                                                    className={cn(
                                                        "rounded-md px-2.5 py-1 text-[11.5px] font-semibold text-muted-foreground",
                                                        (on ? work : "repair") === w &&
                                                            "bg-card text-foreground shadow-sm",
                                                    )}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setWork(w);
                                                        pick(row, w);
                                                    }}
                                                >
                                                    {w === "repair" ? "Repair" : "Replace"}
                                                </button>
                                            ))}
                                        </span>
                                    ) : (
                                        <CornerDownLeft
                                            className={cn("ml-auto h-4 w-4 shrink-0 text-primary", !on && "opacity-0")}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
            <div className="flex flex-wrap gap-3 border-t px-3 py-2 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                    <Kbd>↑</Kbd>
                    <Kbd>↓</Kbd> move
                </span>
                {mode === "repair" && (
                    <span className="inline-flex items-center gap-1">
                        <Kbd>←</Kbd>
                        <Kbd>→</Kbd> repair / replace
                    </span>
                )}
                <span className="inline-flex items-center gap-1">
                    <Kbd>↵</Kbd> pick
                </span>
            </div>
        </div>
    );
}

function Kbd({ children }: { children: ReactNode }) {
    return (
        <kbd className="rounded border border-b-2 bg-muted px-1.5 py-px text-[10px] font-semibold text-muted-foreground">
            {children}
        </kbd>
    );
}

export function SizeBadge({ children, tone }: { children: ReactNode; tone?: "warn" }) {
    return (
        <span
            className={cn(
                "inline-flex h-7 min-w-[38px] shrink-0 items-center justify-center rounded-lg px-1.5 text-xs font-extrabold tabular-nums",
                tone === "warn" ? "bg-warning text-warning-foreground" : "bg-primary/10 text-primary",
            )}
        >
            {children}
        </span>
    );
}

export function DeviceChips({ device }: { device: EditorDevice }) {
    const type = deviceTypeChip(device);
    return (
        <span className="inline-flex flex-wrap gap-1">
            {type && <Chip>{type}</Chip>}
            {device.serial_number && <Chip mono>SN {device.serial_number}</Chip>}
        </span>
    );
}

function Chip({ children, mono }: { children: ReactNode; mono?: boolean }) {
    return (
        <span
            className={cn(
                "whitespace-nowrap rounded-full border bg-muted px-[7px] py-[2px] text-[10.5px] font-semibold text-muted-foreground",
                mono && "font-mono text-[10px] font-medium",
            )}
        >
            {children}
        </span>
    );
}

function DeviceRow({ device }: { device: EditorDevice }) {
    const where = locationOnFile(device.location_description);
    return (
        <>
            <SizeBadge>{sizeBadge(device.size_label) ?? "—"}</SizeBadge>
            <span className="flex min-w-0 flex-col leading-tight">
                <b className="truncate text-[13px] font-bold">
                    {deviceTitle(device)}
                    {where && <span className="font-medium text-muted-foreground"> · {where}</span>}
                </b>
                <span className="mt-[3px]">
                    <DeviceChips device={device} />
                </span>
            </span>
        </>
    );
}
